<template>
    <div>
        <div v-if="loading">
            <loading></loading>
        </div>

        <div v-else>
            <b-alert variant="danger" :show="hasErrors">
                <span v-html="errorMessage"></span>
            </b-alert>

            <b-alert :variant="messageVariant" :show="needsUpdate">
                <p :class="generateWarning ? 'mb-2' : 'mb-0'">
                    {{ updateMessage }}
                </p>
                <p v-if="generateWarning" class="mb-0">
                    ATTENTION : Vous ne pouvez pas générer les échéances :
                </p>
                <ul v-if="generateWarning" class="mb-0">
                    <li v-if="!endOfExercise > 0">
                        La fin d'exercice n'est pas renseignée
                    </li>
                    <li v-if="!this.allRolesAssigned">
                        Certains rôles ne sont pas définis
                    </li>
                </ul>
            </b-alert>

            <!-- filter -->
            <b-row v-if="!hasErrors" class="mb-3">
                <b-col md="6" class="my-1">
                    <multi-deadline-delete-handler
                        v-if="checkedDeadlines.length !== 0"
                    />
                    <charge-summary
                        v-if="checkedDeadlines.length === 0"
                        :year="year"
                        :client-id="client"
                    />
                </b-col>
                <b-col md="6" class="my-1">
                    <b-form-group label-cols-sm="3" class="mb-0">
                        <b-input-group size="sm">
                            <b-form-input
                                v-model="filter"
                                placeholder="Entrer la valeur recherchée"
                            />
                            <b-input-group-append>
                                <b-button
                                    :disabled="!filter"
                                    @click="filter = ''"
                                    >Effacer
                                </b-button>
                            </b-input-group-append>
                        </b-input-group>
                    </b-form-group>
                </b-col>
            </b-row>
            <!-- /filter -->

            <!-- Table -->
            <b-table
                :key="tableKey"
                responsive
                small
                :items="filteredItems"
                :fields="fields"
                :filter-included-fields="['search']"
                :filter="filter"
                :per-page="perPage"
                :current-page="currentPage"
                primary-key="id"
                @filtered="onFiltered"
            >
                <!-- HEADER -->
                <template #head(id)="data">
                    <b-form-checkbox v-model="status" @input="toggleSelect" />
                </template>

                <template #cell(id)="data">
                    <deadline-delete-check-box
                        :id="data.item.id"
                        :key="'mddh' + data.item.id"
                        :selected="data.item.selected"
                    />
                </template>
                <template #cell(title)="data">
                    {{ data.item.title }}
                </template>
                <template #cell(assigned_to)="data">
                    <assignment-form :deadline="data.item" />
                </template>
                <template #cell(duration)="data">
                    <duration-form :deadline="data.item" />
                </template>
                <template #cell(deadline_date)="data">
                    <deadline-date-form :deadline="data.item" />
                </template>
            </b-table>
            <!-- /Table -->

            <!-- Pagination -->
            <b-row>
                <b-col md="3" class="my-1 text-center">
                    <b-pagination
                        v-if="totalRows > 0"
                        v-model="currentPage"
                        :total-rows="totalRows"
                        :per-page="perPage"
                        class="my-0"
                        size="sm"
                    />
                </b-col>
                <b-col md="6" class="my-2">
                    <p class="text-center small pt-0">{{ legend }}</p>
                </b-col>
                <b-col md="3" class="my-1 text-right">
                    <b-dropdown
                        right
                        split
                        size="sm"
                        :text="perPage + ' éléments par page'"
                    >
                        <b-dropdown-item
                            v-for="pageOption in pageOptions"
                            :key="pageOption"
                            @click="setPerPage(pageOption)"
                        >
                            {{ pageOption }}
                        </b-dropdown-item>
                    </b-dropdown>
                </b-col>
            </b-row>
            <!-- /Pagination -->
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'

import loading from '../../../common/components/loading.vue'
import DurationForm from '../components/deadlines/DurationForm.vue'
import DeadlineDateForm from '../components/deadlines/DeadlineDateForm.vue'
import AssignmentForm from '../components/deadlines/AssignmentForm.vue'
import MultiDeadlineDeleteHandler from '../components/deadlines/MultiDeadlineDeleteHandler.vue'
import DeadlineDeleteCheckBox from '../components/deadlines/DeadlineDeleteCheckBox.vue'

import TableHandler from '../../../common/mixins/TableHandler.vue'
import ChargeSummary from '../components/deadlines/ChargeSummary.vue'

export default {
    name: 'TableDeadlines',

    components: {
        ChargeSummary,
        MultiDeadlineDeleteHandler,
        DeadlineDeleteCheckBox,
        AssignmentForm,
        DeadlineDateForm,
        DurationForm,
        loading,
    },

    mixins: [TableHandler],

    props: {
        client: {
            type: Number,
            required: true,
        },
        endOfExercise: {
            type: Number,
            required: true,
        },
        generateButtonState: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            tableKey: 'table',
            status: false,
            fields: [
                {
                    key: 'id',
                    label: '',
                    class: 'td-icon text-center',
                },
                {
                    key: 'title',
                    label: 'Action',
                    class: 'td-sm',
                    sortable: true,
                },
                {
                    key: 'assigned_to',
                    label: 'A faire par',
                    class: 'td-sm td-assignment',
                    sortable: true,
                },
                {
                    key: 'deadline_date',
                    label: 'Echéance',
                    class: 'td-sm td-deadline',
                    sortable: true,
                },
                {
                    key: 'duration',
                    label: 'Durée',
                    class: 'td-sm td-duration',
                    sortable: true,
                },
            ],
            startOfExercise: String,
        }
    },

    computed: {
        ...mapState({
            year: (state) => state.browsing.year,
            allRolesAssigned: (state) => state.browsing.allRolesAssigned,
            needsUpdate: (state) => state.browsing.needsUpdate,
            checkedDeadlines: (state) => state.data.checkedDeadlines,
            deadlinesTableFilter: (state) =>
                state.browsing.deadlinesTableFilter,
        }),

        generateWarning() {
            return this.generateButtonState === 'disabled'
        },

        messageVariant() {
            return this.generateWarning ? 'warning' : 'primary'
        },

        updateMessage() {
            let message =
                "Des échéances doivent être générées pour l'exercice commençant le "
            message +=
                this.$moment(this.startOfExercise).format('Do MMMM ') +
                this.year
            return message
        },
    },

    async created() {
        this.$store.commit('browsing/SET_PAGE_NAME', 'clientDeadlines')
        this.$store.commit('data/RESET_CHECKED_DEADLINES')
        await this.getItems()
    },

    mounted() {
        this.setStartOfExercise()
        this.filter = this.deadlinesTableFilter
        this.$store.commit('browsing/RESET_DEADLINES_TABLE_FILTER')
    },

    methods: {
        toggleSelect() {
            this.$store.commit('data/RESET_CHECKED_DEADLINES')
            const selectedItems = this.items.filter((item) =>
                item.search.includes(this.filter.toUpperCase())
            )
            if (this.status) {
                selectedItems.forEach((item) =>
                    this.$store.commit('data/ADD_DEADLINE', item.id)
                )
            }
            selectedItems.forEach((item) => (item.selected = this.status))
            this.tableKey = Date()
        },

        confirmDelete() {
            const selectedItems = this.items.filter((item) =>
                item.title.includes(this.filter)
            )
            const modalMessage = `Etes-vous sûr de vouloir supprimer ces ${selectedItems.length} lignes ?`
            this.$bvModal
                .msgBoxConfirm(modalMessage, {
                    title: "Suppression d'échéances",
                    size: 'sm',
                    buttonSize: 'sm',
                    okVariant: 'danger',
                    okTitle: 'OUI',
                    cancelTitle: 'NON',
                    footerClass: 'p-2',
                    hideHeaderClose: false,
                    centered: true,
                })
                .then((value) => {
                    if (value) {
                        this.deleteDeadlines()
                    }
                })
                .catch((err) => {
                    // An error occurred
                })
        },

        setStartOfExercise() {
            let month = 0
            if (this.endOfExercise === 12) {
                month = 1
            } else {
                month = this.endOfExercise + 1
            }
            if (month < 10) {
                this.startOfExercise = new Date(`${this.year}-0${month}-01`)
            } else {
                this.startOfExercise = new Date(`${this.year}-${month}-01`)
            }
        },

        async getItems() {
            await this.checkUpdates()
            try {
                this.loading = true
                const response = await axios.get(
                    `/clients/${this.client}/clientDeadlines/${this.year}`
                )
                this.items = response.data.data
                this.filteredItems = this.items
                this.updateTotalRows()
                this.loading = false
            } catch (error) {
                // handle error
                console.log(error)
                this.handleErrors(error.response)
            }
        },

        async checkUpdates() {
            try {
                const response = await axios.get(
                    `/clients/${this.client}/clientDeadlines/${this.year}/check-for-update`
                )
                this.$store.commit(
                    'browsing/SET_NEEDS_UPDATE',
                    response.data.needsUpdate
                )
                this.$store.commit(
                    'browsing/SET_ALL_ROLES_ASSIGNED',
                    response.data.allRolesAssigned
                )
            } catch (error) {
                // handle error
                console.log(error)
                this.handleErrors(error.response)
            }
        },
    },
}
</script>

<style scoped></style>
